import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ExternalLink, Terminal } from "lucide-react";
import { devices } from "@/pages/Devices";

const guides = {
  "heltec-v3-v4": {
    overview: "The Heltec WiFi LoRa 32 V3/V4 is a workhorse ESP32-S3 with built-in OLED, USB-C, and an SX1262 LoRa transceiver. Best balance of features and price.",
    flash: [
      "Connect the board to your computer with a quality USB-C cable (data — not charge-only).",
      "Open Chrome and visit flasher.meshcore.io",
      "Click 'Select your board' → choose 'Heltec V3' or 'Heltec V4'.",
      "Choose firmware variant: Companion (for personal use) or Repeater (for fixed install).",
      "Click 'Flash' and approve the serial port dialog. Wait ~30 seconds.",
    ],
    cli: `# Optional: manual flash using esptool
pip install esptool
esptool.py --chip esp32s3 --port /dev/ttyUSB0 erase_flash
esptool.py --chip esp32s3 --port /dev/ttyUSB0 \\
  --baud 921600 write_flash 0x0 meshcore-heltec-v3.bin`,
    notes: ["Hold BOOT before plugging in if the board isn't detected.", "On macOS, install the CP210x or CH340 USB-Serial driver if /dev/cu.usbserial-* doesn't appear."],
  },
  "rak4631": {
    overview: "The RAK4631 uses an nRF52840 with the SX1262 LoRa chip. Its ultra-low sleep current is unmatched — companion nodes routinely run 2–3 weeks on a 2000mAh LiPo.",
    flash: [
      "Plug the RAK4631 into your computer via USB-C.",
      "Visit the meshcore.io flashing website",
      "Select your device 'RAK WisBlock / WisMesh (RAK 4631)'",
      "Choose what firmware you want based on the device you want to create.",
      "Enter DFU Mode",
      "Flash!"
    ],
    cli: `Download the u2f file from the meshcore.io flasher site
double press the reset button on the 4631 until a drive appears on your system
drag the u2f file to the new drive that appeared.`,
    notes: ["If you don't see the UF2 drive, the bootloader may be missing — re-flash with the official RAK bootloader first.", "Pair LiPo (3.7V, JST) for portable operation.", "Check your battery polarity before connecting. You can fry the charge circuit", "We strongly recommend installing <a class='text-[#FF5500]' href='https://flasher.meshcore.io/firmware/wiscore_rak4631_board_bootloader-0.9.2-OTAFIX2.2.uf2' target='_blank'>OTAFIX bootloader</a> by <a class='text-[#FF5500]' href='https://github.com/oltaco' target='_blank'>@oltaco</a> for more reliable Bluetooth OTA DFU."],
  },
  "t-beam": {
    overview: "The LilyGo T-Beam combines an ESP32, SX1276/SX1262 LoRa radio, and an onboard u-blox GPS. Excellent for portable use and surveying repeater sites.",
    flash: [
      "Connect via USB. Most newer revisions show as /dev/cu.usbserial- or /dev/ttyUSB0.",
      "Open flasher.meshcore.io and pick 'T-Beam (your version)'.",
      "Variant: most users want 'Companion w/ GPS' to broadcast position to room servers.",
      "Click Flash, accept the port prompt, wait for completion.",
    ],
    cli: `# Manual flash (T-Beam v1.1):
esptool.py --chip esp32 --port /dev/ttyUSB0 erase_flash
esptool.py --chip esp32 --port /dev/ttyUSB0 \\
  --baud 921600 write_flash 0x1000 meshcore-tbeam.bin`,
    notes: ["Always insert an 18650 battery before flashing — the USB port alone can brown out the GPS module.", "GPS first-fix can take several minutes outdoors."],
  },
  "xiao-s3-wio": {
    overview: "The Seeed Xiao ESP32S3 with the Wio-SX1262 expansion is the tiniest LoRa MeshCore platform we use. Perfect for stealth installs.",
    flash: [
      "Connect the Xiao to your PC via USB-C.",
      "If not detected, hold the small BOOT button while plugging in to force bootloader mode.",
      "On flasher.meshcore.io select 'Xiao ESP32S3 + Wio SX1262'.",
      "Pick Companion firmware (battery use) or Repeater (24/7 powered).",
      "Click Flash and wait for completion.",
    ],
    cli: `esptool.py --chip esp32s3 --port /dev/ttyACM0 erase_flash
esptool.py --chip esp32s3 --port /dev/ttyACM0 \\
  --baud 921600 write_flash 0x0 meshcore-xiao-s3.bin`,
    notes: ["The Xiao has only 8 GPIO accessible — confirm your antenna and battery wiring against the Wio pinout.", "Add an external 3D-printed antenna mount for any outdoor install."],
  },
};

export default function DeviceDetail() {
  const { slug } = useParams();
  const device = devices.find((d) => d.slug === slug);
  const guide = guides[slug];

  if (!device || !guide) return <Navigate to="/devices" replace />;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24" data-testid={`device-detail-${slug}`}>
      <Link to="/devices" className="inline-flex items-center gap-2 text-sm text-[#94A3B8] hover:text-white mb-6">
        <ArrowLeft className="w-4 h-4" /> All devices
      </Link>

      <div className="overline mb-3">/device — {device.chipset}</div>
      <h1 className="font-display font-black text-white text-4xl sm:text-5xl lg:text-6xl tracking-tighter mb-6">
        {device.name}
      </h1>

      <div className="grid sm:grid-cols-3 gap-4 mb-10 font-mono text-xs">
        <div className="bg-[#13161C] border border-[#272E3A] p-4">
          <div className="text-[#94A3B8] uppercase tracking-widest text-[10px] mb-1">Price</div>
          <div className="text-white text-lg">{device.price}</div>
        </div>
        <div className="bg-[#13161C] border border-[#272E3A] p-4">
          <div className="text-[#94A3B8] uppercase tracking-widest text-[10px] mb-1">Best for</div>
          <div className="text-white text-lg">{device.bestFor}</div>
        </div>
        <div className="bg-[#13161C] border border-[#272E3A] p-4">
          <div className="text-[#94A3B8] uppercase tracking-widest text-[10px] mb-1">Chipset</div>
          <div className="text-white text-lg">{device.chipset}</div>
        </div>
      </div>

      <p className="text-lg text-slate-300 leading-relaxed mb-10">{guide.overview}</p>

      <section className="mb-12">
        <h2 className="font-display font-bold text-white text-2xl mb-5">Flashing — Web flasher (recommended)</h2>
        <ol className="space-y-3">
          {guide.flash.map((step, i) => (
            <li key={i} className="flex gap-4">
              <span className="font-mono text-[#FF5500] text-sm font-bold mt-0.5 shrink-0">{String(i + 1).padStart(2, "0")}</span>
              <span className="text-slate-300">{step}</span>
            </li>
          ))}
        </ol>
        <a
          href="http://flasher.meshcore.io/"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 mt-6 px-5 py-2.5 bg-[#FF5500] hover:bg-[#FF7326] text-white font-semibold text-sm transition-colors"
        >
          Open Web Flasher <ExternalLink className="w-3 h-3" />
        </a>
      </section>

      <section className="mb-12">
        <div className="flex items-center gap-2 mb-4">
          <Terminal className="w-5 h-5 text-[#FF5500]" />
          <h2 className="font-display font-bold text-white text-2xl">Manual flashing</h2>
        </div>
        <pre className="bg-[#0A0C10] border border-[#272E3A] p-5 rounded overflow-x-auto">
          <code className="font-mono text-sm text-slate-200 whitespace-pre">{guide.cli}</code>
        </pre>
      </section>

      <section className="mb-12">
        <h2 className="font-display font-bold text-white text-2xl mb-4">Notes & gotchas</h2>
        <ul className="space-y-2">
          {guide.notes.map((n, i) => (
            <li key={i} className="flex gap-3 text-slate-300">
              <span className="text-[#FF5500] font-mono">›</span>
              <span dangerouslySetInnerHTML={{ __html: n }} />
            </li>
          ))}
        </ul>
      </section>

      <div className="bg-[#13161C] border border-[#272E3A] p-6 lg:p-8 flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <div>
          <div className="overline mb-1">Next step</div>
          <div className="font-display font-bold text-white text-lg">Apply East Coast preset</div>
        </div>
        <Link
          to="/settings"
          data-testid="next-settings-btn"
          className="px-5 py-3 bg-[#FF5500] hover:bg-[#FF7326] text-white font-semibold whitespace-nowrap transition-colors">
          Radio settings →
        </Link>
      </div>
    </div>
  );
}
